import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { 
  login as apiLogin, 
  registerOwner as apiRegister,
  socialLogin as apiSocialLogin 
} from "../authApi";
import {
  sendOtp as apiSendOtp,
  verifyOtpLogin as apiVerifyOtpLogin,
  verifyOtpRegister as apiVerifyOtpRegister,
} from "../authOtpApi";
import { devWarn } from "../utils/devLog";

const AuthContext = createContext(null);

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("auth_user") || localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getStoredToken = () => {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
};

const persistSession = (user, token) => {
  if (token) localStorage.setItem("auth_token", token);
  if (user) localStorage.setItem("auth_user", JSON.stringify(user));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(false);

  const applySession = useCallback((nextUser, nextToken) => {
    persistSession(nextUser, nextToken);
    setUser(nextUser || null);
    setToken(nextToken || null);
    // Dispatch event to notify wishlist to refresh
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth-changed"));
    }
  }, []);

  const logout = useCallback(async () => {
    // Call logout API to invalidate token on server
    try {
      const { logout: apiLogout } = await import("../authApi");
      await apiLogout();
    } catch (error) {
      // Even if API call fails, continue with local logout
      devWarn("Logout API call failed:", error);
    }
    
    // Clear local storage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    setUser(null);
    setToken(null);
    // Dispatch event to notify wishlist to clear
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth-changed"));
    }
  }, []);

  const callAuthApi = async (exec, fallbackMessage) => {
    setLoading(true);
    try {
      const response = await exec();
      const nextUser = response?.user || response?.data?.user || response?.data;
      const nextToken = response?.token || response?.access_token || response?.data?.token;
      if (nextToken) {
        applySession(nextUser, nextToken);
      } else if (nextUser) {
        localStorage.setItem("auth_user", JSON.stringify(nextUser));
        setUser(nextUser);
      }
      return response;
    } catch (error) {
      const api = error?.response?.data;
      const message =
        api?.message ||
        (api?.errors && Object.values(api.errors).flat()[0]) ||
        error?.message ||
        fallbackMessage;
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(
    (payload) => callAuthApi(() => apiLogin(payload), "Login failed"),
    []
  );

  const register = useCallback(
    (payload) =>
      callAuthApi(() => apiRegister(payload), "Registration failed"),
    []
  );

  const socialLogin = useCallback((provider) => {
    return apiSocialLogin(provider);
  }, []);

  const sendOtp = useCallback(async (payload) => {
    setLoading(true);
    try {
      return await apiSendOtp(payload);
    } catch (error) {
      const api = error?.response?.data;
      const message =
        api?.message ||
        (api?.errors && Object.values(api.errors).flat()[0]) ||
        error?.message ||
        "Could not send OTP";
      const err = new Error(message);
      if (error?.response) err.response = error.response;
      if (error?.retryAfterSeconds) err.retryAfterSeconds = error.retryAfterSeconds;
      const retryFromBody =
        api?.retry_after_seconds ??
        api?.retry_after ??
        api?.data?.retry_after_seconds;
      if (!err.retryAfterSeconds && retryFromBody != null) {
        const n = parseInt(retryFromBody, 10);
        if (Number.isFinite(n) && n > 0) err.retryAfterSeconds = n;
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtpLogin = useCallback(
    (payload) => callAuthApi(() => apiVerifyOtpLogin(payload), "OTP login failed"),
    []
  );

  const verifyOtpRegister = useCallback(
    (payload) => callAuthApi(() => apiVerifyOtpRegister(payload), "OTP registration failed"),
    []
  );

  useEffect(() => {
    // 1. Check for token in URL (e.g. after social login redirect)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token") || params.get("access_token");
    if (urlToken) {
      // Remove token from URL for security and cleanliness
      const nextUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, nextUrl);
      
      // We don't have the user object yet, so we just set the token
      // The app will likely fetch the user profile next or use the token for API calls
      setToken(urlToken);
      localStorage.setItem("auth_token", urlToken);
      
      // If backend also sent user data in URL (optional but common)
      const urlUser = params.get("user");
      if (urlUser) {
        try {
          const parsedUser = JSON.parse(decodeURIComponent(urlUser));
          setUser(parsedUser);
          localStorage.setItem("auth_user", JSON.stringify(parsedUser));
        } catch (e) {
          console.error("Failed to parse user from URL", e);
        }
      }
      
      // Notify other parts of the app
      window.dispatchEvent(new CustomEvent("auth-changed"));
      
      // Check for redirect path after social login
      const redirectPath = sessionStorage.getItem("login_redirect_path");
      if (redirectPath) {
        sessionStorage.removeItem("login_redirect_path");
        // Use setTimeout to ensure navigation happens after state updates
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      }
    }

    // 2. Listen for auth changes from other tabs
    const storageHandler = (event) => {
      if (["auth_user", "auth_token", "user"].includes(event.key)) {
        setUser(getStoredUser());
        setToken(getStoredToken());
      }
    };
    window.addEventListener("storage", storageHandler);

    // 3. Listen for auth-changed events (same-tab login, logout, profile edits, stale-session clear)
    const authChangedHandler = () => {
      setUser(getStoredUser());
      setToken(getStoredToken());
    };
    window.addEventListener("auth-changed", authChangedHandler);

    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener("auth-changed", authChangedHandler);
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      login,
      register,
      sendOtp,
      verifyOtpLogin,
      verifyOtpRegister,
      socialLogin,
      logout,
      loading,
    }),
    [user, token, login, register, sendOtp, verifyOtpLogin, verifyOtpRegister, socialLogin, logout, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthContext;

